"use client";

import { useCallback, useEffect, useState, useTransition } from "react";
import { Search } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { listThreads } from "@/actions/chat";
import type { ChatThread, ThreadType } from "@/types/chat";

export const THREAD_LABELS: Record<string, string> = {
  appointment: "Запись",
  task: "Задача",
  work_item: "Работа",
};

const POLL_INTERVAL_MS = 8000;

/**
 * Searchable chat thread list shared by the floating and mobile chats.
 * Loads and polls threads while `active` is true.
 */
export default function ThreadList({
  active,
  onSelect,
}: {
  /** True while the list view is visible — controls loading and polling. */
  active: boolean;
  onSelect: (thread: { type: ThreadType; id: string }) => void;
}) {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [search, setSearch] = useState("");
  const [, startTransition] = useTransition();

  const loadThreads = useCallback(() => {
    startTransition(async () => {
      const t = await listThreads();
      setThreads(t);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    loadThreads();
    const interval = setInterval(loadThreads, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [active, loadThreads]);

  const filtered = threads.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.thread_id.toLowerCase().includes(q) ||
      t.last_author_name.toLowerCase().includes(q) ||
      t.last_content.toLowerCase().includes(q) ||
      (THREAD_LABELS[t.thread_type] ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Search */}
      <div className="border-b px-3 py-2">
        <div className="relative">
          <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск чатов..."
            className="w-full rounded-md border bg-transparent py-1.5 pr-3 pl-8 text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
          />
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <EmptyState
            title={threads.length === 0 ? "Нет активных чатов" : "Ничего не найдено"}
            className="py-10"
          />
        )}
        {filtered.map((t) => (
          <button
            key={`${t.thread_type}-${t.thread_id}`}
            onClick={() => onSelect({ type: t.thread_type, id: t.thread_id })}
            className="w-full border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                {THREAD_LABELS[t.thread_type] ?? t.thread_type}
              </span>
              <span className="text-[10px] text-muted-foreground/60">
                {new Date(t.last_created_at).toLocaleString("ru-RU", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm font-medium">
              {t.last_author_name}
            </p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {t.last_content}
            </p>
          </button>
        ))}
      </div>

      {/* New chat shortcut */}
      <div className="border-t px-3 py-2">
        <p className="text-center text-[10px] text-muted-foreground">
          Откройте запись или задачу, чтобы начать новый чат
        </p>
      </div>
    </div>
  );
}
